import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Shield, ArrowLeft, Loader2, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { tokenStore } from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("Missing reset token.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data?.access_token) tokenStore.set(data.access_token);
      if (data?.user) setUser(data.user);
      toast.success("Password updated. Welcome back, hero!");
      navigate("/", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-theme min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <div className="max-w-md w-full">
        <Link
          to="/login"
          className="mb-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900"
          data-testid="reset-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-slate-900 text-amber-400 flex items-center justify-center ring-4 ring-slate-900/5">
            <KeyRound className="w-6 h-6" strokeWidth={2.6} />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600">My Money Hero</p>
            <h1 className="text-2xl font-black text-slate-900 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Reset password
            </h1>
          </div>
        </div>

        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-sm font-medium text-slate-600 leading-relaxed">
              Choose a new password for your account. You'll be signed in automatically.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">New password</Label>
              <Input
                type="password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters" className="h-11 rounded-xl"
                data-testid="reset-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Confirm password</Label>
              <Input
                type="password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again" className="h-11 rounded-xl"
                data-testid="reset-password-confirm-input"
              />
            </div>

            {err && (
              <div className="text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="reset-error">
                {err}
              </div>
            )}

            <Button
              type="submit" disabled={busy || !token}
              className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black tracking-wide"
              data-testid="reset-submit-button"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="inline-flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400" /> Update password
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
